// File: frontend/src/components/marketing/PublicLayout.js
//
// Shared shell for public pages: skip link, sticky marketing header, main
// content, footer, floating WhatsApp link, SEO metadata, and reduced-motion-aware
// smooth anchor scrolling. It does NOT use the authenticated sidebar layout.
// Auth state is read from the app's existing mechanism
// (authService.getCurrentUser) to swap the CTAs.
//
// Canonical URL: set for the indexable public pages (config/publicPages.json)
// and nothing else. A page that must stay out of search (the 404 page) passes
// `noindex`.

import React, { useEffect } from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import MarketingHeader from "./MarketingHeader";
import MarketingFooter from "./MarketingFooter";
import SkipToContent, { MAIN_CONTENT_ID } from "./SkipToContent";
import WhatsAppCta from "./WhatsAppCta";
import { authService } from "../../services/api";
import usePageMeta from "../../utils/usePageMeta";
import { mainUrl } from "../../config/origins";
import { isIndexablePath, normalisePath } from "../../publicRoutes";

export const DEFAULT_TITLE = "PesaFii | Hotspot Billing and Mobile Money for Wi-Fi Businesses";
export const DEFAULT_DESCRIPTION =
  "PesaFii helps hotspot operators in Uganda sell Wi-Fi packages and vouchers, collect MTN Mobile Money and Airtel Money payments, manage MikroTik routers and track revenue from one dashboard.";

// A /#section link arriving from another page lands before the section exists
// (the page renders after navigation), so the browser's own jump can miss it.
function useScrollToHashOnArrival() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || hash.length < 2) return undefined;
    const id = decodeURIComponent(hash.slice(1));
    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView();
    });
    return () => cancelAnimationFrame(frame);
    // Arrival only: in-page anchor clicks are handled by the browser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default function PublicLayout({ children, title, description, navLinks, noindex = false, whatsappMessage }) {
  // Same auth-state mechanism the app's PrivateRoute uses.
  const isAuthed = Boolean(authService.getCurrentUser());
  const { pathname } = useLocation();
  const canonical = !noindex && isIndexablePath(pathname) ? mainUrl(normalisePath(pathname)) : undefined;

  usePageMeta({
    title: title || DEFAULT_TITLE,
    description: description || DEFAULT_DESCRIPTION,
    // The same share image public/index.html gives crawlers: absolute, raster,
    // 1200×630. An SVG or relative path here would be ignored by the apps that
    // do read runtime tags.
    ogImage: "https://pesafii.net/pesafii-share.png",
    canonical,
    noindex,
  });
  useScrollToHashOnArrival();

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default", color: "text.primary",
      // clip, not hidden: `hidden` makes this box a scroll container and every
      // position: sticky inside it (feature panel, product tour) stops working.
      overflowX: "hidden",
      "@supports (overflow: clip)": { overflowX: "clip" },
    }}>
      {/* Smooth scroll for in-page anchors, disabled for reduced-motion users. */}
      <style>{`@media (prefers-reduced-motion: no-preference){html{scroll-behavior:smooth}}`}</style>
      <SkipToContent />
      {/* navLinks is optional — MarketingHeader falls back to the site-wide
          links, which reach the "/" page's sections from any page. */}
      <MarketingHeader isAuthed={isAuthed} navLinks={navLinks} />
      <Box component="main" id={MAIN_CONTENT_ID} tabIndex={-1} sx={{ flex: 1, minWidth: 0, "&:focus": { outline: "none" } }}>
        {children}
      </Box>
      <MarketingFooter isAuthed={isAuthed} />
      <WhatsAppCta message={whatsappMessage} />
    </Box>
  );
}
